import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, Volume2, Square, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import BLOG_FALLBACK from "@/data/blogFallback";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stripMarkdown = (md) =>
  md.replace(/[#*_`>|[\]()-]/g, " ").replace(/\s+/g, " ").trim();

const BlogArticle = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const listen = async () => {
    if (playing && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlaying(false);
      return;
    }
    setAudioLoading(true);
    try {
      const res = await fetch(`${API}/voice/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: `${article.title}. ${stripMarkdown(article.content)}` }),
      });
      if (!res.ok) throw new Error("tts failed");
      const blob = await res.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;
      audio.onended = () => setPlaying(false);
      await audio.play();
      setPlaying(true);
    } catch {
      toast.error("Audio non disponibile, riprova tra poco.");
    } finally {
      setAudioLoading(false);
    }
  };

  useEffect(() => {
    fetch(`${API}/blog/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error("404");
        return r.json();
      })
      .then((data) => {
        setArticle(data);
        setNotFound(false);
      })
      .catch(() => {
        const fallback = BLOG_FALLBACK.find((a) => a.slug === slug);
        if (fallback) setArticle(fallback);
        else setNotFound(true);
      });
  }, [slug]);

  useEffect(() => {
    if (!article) return undefined;
    const prevTitle = document.title;
    document.title = article.meta_title || article.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content") || "";
    if (meta && article.meta_description) meta.setAttribute("content", article.meta_description);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "article-jsonld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.meta_description,
      datePublished: article.published_at,
      author: { "@type": "Organization", name: "XSALES CONSULTING LTD" },
      ...(article.image ? { image: article.image } : {}),
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      if (meta) meta.setAttribute("content", prevDesc);
      document.getElementById("article-jsonld")?.remove();
    };
  }, [article]);

  return (
    <div className="bg-void text-white font-body min-h-screen">
      <div className="grain-overlay" />
      <div className="max-w-3xl mx-auto px-6 py-20">
        <a href="/blog" data-testid="article-back-link" className="inline-flex items-center gap-2 text-sm text-neon-cyan hover:gap-3 transition-all mb-10">
          <ArrowLeft size={16} /> Blog
        </a>
        {notFound && <p className="text-white/50" data-testid="article-not-found">Articolo non trovato.</p>}
        {!article && !notFound && (
          <div data-testid="article-loading" aria-busy="true" className="py-20 text-center">
            <p className="text-white/40 text-sm animate-pulse">Caricamento dell'articolo...</p>
          </div>
        )}
        {article && (
          <>
          <Seo title={`${article.meta_title || article.title} | XS Consulting`} description={article.meta_description} path={`/blog/${slug}`} />
          <article data-testid="blog-article">
            <div className="flex flex-wrap gap-2 mb-5">
              {article.tags?.map((t) => (
                <span key={t} className="text-[10px] hud-label uppercase border border-neon-cyan/30 text-neon-cyan rounded-full px-3 py-1">{t}</span>
              ))}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-4">{article.title}</h1>
            <p className="text-sm text-white/40 mb-6">
              {article.published_at ? new Date(article.published_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : ""}
            </p>
            {article.image && (
              <img
                src={article.image}
                alt={`Copertina articolo: ${article.title}`}
                loading="lazy"
                data-testid="article-cover-image"
                className="w-full h-64 sm:h-80 object-cover rounded-2xl border border-white/10 mb-8"
              />
            )}
            <button
              onClick={listen}
              disabled={audioLoading}
              data-testid="article-listen-button"
              className="inline-flex items-center gap-2 border border-white/20 rounded-full px-6 py-3 text-sm font-semibold mb-10 hover:border-neon-cyan/60 hover:text-neon-cyan transition-all duration-300 disabled:opacity-50"
            >
              {audioLoading ? (
                <><Loader2 size={16} className="animate-spin" /> Sto generando l'audio...</>
              ) : playing ? (
                <><Square size={16} /> Ferma la lettura</>
              ) : (
                <><Volume2 size={16} /> Ascolta l'articolo</>
              )}
            </button>
            <div className="md-content text-white/75 leading-relaxed">
              <ReactMarkdown
                components={{
                  a: ({ href, children }) =>
                    href?.startsWith("http") ? (
                      <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
                    ) : (
                      <a href={href}>{children}</a>
                    ),
                }}
              >
                {article.content}
              </ReactMarkdown>
            </div>
            <div className="mt-14 glass-panel rounded-2xl p-8 text-center">
              <p className="font-display text-xl font-bold mb-3">Parliamone di persona</p>
              <p className="text-sm text-white/60 mb-6">Consulenza gratuita, senza impegno.</p>
              <a
                href="https://wa.me/41782042172?text=Ciao%20XS%20Consulting!%20Ho%20letto%20il%20vostro%20articolo%20e%20vorrei%20approfondire."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="article-whatsapp-cta"
                className="inline-flex items-center gap-2 btn-cyber bg-gradient-to-r from-neon-cyan to-neon-violet px-7 py-3.5 font-semibold text-void hover:opacity-90 transition-opacity"
              >
                <MessageCircle size={17} /> Scrivici su WhatsApp
              </a>
            </div>
          </article>
          </>
        )}
      </div>
    </div>
  );
};

export default BlogArticle;
